import CustomTooltip from '../../shared/CustomTooltip';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Avatar,
    Box,
    FormControlLabel,
    FormHelperText,
    InputLabel,
    ListItemAvatar,
    ListItemText,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    styled,
    useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useGetIndividualOwnerAssetsQuery } from '../../../app/Slices/SevenDayApiSlice';
import UserRole from '../../../shared/AuthRoles';
import { FormValues } from '../../types/experience';
import FormMultiAutocomplete from '../../shared/FormMultiAutocomplete';
import FormCheckbox from '../../shared/FormCheckbox';
import { HelpOutline, KeyboardArrowDown, Tune } from '@mui/icons-material';

const StyledTableCell = styled(TableCell)({
    paddingLeft: 0,
    paddingRight: 0,
});

const ReservationNotifications = () => {
    const loggedInUser = UserRole();
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [experiencesOpen, setExperiencesOpen] = useState(false);

    const { data: experiences } = useGetIndividualOwnerAssetsQuery(
        {
            url: `owners/${loggedInUser?.company}/modules`,
            sort: 'name',
            status: 'published',
        },
        { skip: !loggedInUser?.role.includes('owner') },
    );

    const { control, setValue } = useFormContext();

    const reservationNotificationModules = useWatch({
        name: 'reservationNotificationModules',
        control,
    });

    const handleExperiences = () => {
        if (experiencesOpen) setValue('reservationNotificationModules', []);

        setExperiencesOpen(!experiencesOpen);
    };

    const theme = useTheme();

    useEffect(() => {
        if (reservationNotificationModules?.length > 0)
            setExperiencesOpen(true);
    }, [reservationNotificationModules]);

    return (
        <div>
            <Table>
                <TableHead>
                    <TableRow>
                        <StyledTableCell
                            sx={{ width: '70%', borderBottom: 'none', py: 0 }}
                        >
                            <Typography variant='subHeading'>
                                Reservation Notifications
                            </Typography>
                        </StyledTableCell>
                        <StyledTableCell
                            padding='checkbox'
                            align='center'
                            sx={{ borderBottom: 'none' }}
                        >
                            Email
                        </StyledTableCell>
                        <StyledTableCell
                            padding='checkbox'
                            align='center'
                            sx={{ borderBottom: 'none' }}
                        >
                            Mobile
                        </StyledTableCell>
                        <StyledTableCell
                            padding='checkbox'
                            align='center'
                            sx={{ borderBottom: 'none' }}
                        >
                            In-app
                        </StyledTableCell>
                    </TableRow>
                </TableHead>

                <TableBody>
                    <TableRow>
                        <StyledTableCell>
                            <Typography>Experience Review </Typography>
                            Receive Experience Review Emails
                        </StyledTableCell>
                        <StyledTableCell padding='checkbox' align='center'>
                            <FormCheckbox
                                name='reviewRequestEmailNotification'
                                control={control}
                            />
                        </StyledTableCell>
                        <StyledTableCell padding='checkbox' align='center'>
                            N/A
                        </StyledTableCell>
                        <StyledTableCell padding='checkbox' align='center'>
                            N/A
                        </StyledTableCell>
                    </TableRow>
                    {(loggedInUser?.role.includes('guest') ||
                        loggedInUser?.role.includes('owner')) && (
                        <TableRow>
                            <StyledTableCell>
                                <Typography>Access requests</Typography>
                                Get notified when someone requests
                                face-recognition access to your reservation
                            </StyledTableCell>
                            <StyledTableCell padding='checkbox' align='center'>
                                <FormCheckbox
                                    name='accessRequestEmailNotification'
                                    control={control}
                                />
                            </StyledTableCell>
                            <StyledTableCell padding='checkbox' align='center'>
                                <FormCheckbox
                                    name='accessRequestPushNotification'
                                    control={control}
                                />
                            </StyledTableCell>
                            <StyledTableCell padding='checkbox' align='center'>
                                <FormCheckbox
                                    name='accessRequestAppNotification'
                                    control={control}
                                />
                            </StyledTableCell>
                        </TableRow>
                    )}
                    <TableRow>
                        <StyledTableCell sx={{ borderBottom: 'none' }}>
                            <Typography>
                                Activity{' '}
                                <CustomTooltip
                                    title={
                                        <Typography>
                                            Receive notifications for updates
                                            made to your reservations, e.g.,{' '}
                                            {loggedInUser.role.includes('owner')
                                                ? 'requests, '
                                                : ''}
                                            amendments, status updates and
                                            cancellations
                                        </Typography>
                                    }
                                    placement='right'
                                >
                                    <HelpOutline
                                        sx={{
                                            color: 'text.primary',
                                            fontSize: '16px',
                                        }}
                                    />
                                </CustomTooltip>
                            </Typography>
                            Get notified about reservation updates
                        </StyledTableCell>
                        <StyledTableCell
                            padding='checkbox'
                            align='center'
                            sx={{ borderBottom: 'none' }}
                        >
                            {loggedInUser?.role.includes('guest') ||
                            loggedInUser?.role.includes('external_attendee') ? (
                                <FormCheckbox
                                    name='reservationEmailNotification'
                                    control={control}
                                />
                            ) : (
                                'N/A'
                            )}
                        </StyledTableCell>
                        <StyledTableCell
                            padding='checkbox'
                            align='center'
                            sx={{ borderBottom: 'none' }}
                        >
                            <FormCheckbox
                                name='reservationPushNotification'
                                control={control}
                            />
                        </StyledTableCell>
                        <StyledTableCell
                            padding='checkbox'
                            align='center'
                            sx={{ borderBottom: 'none' }}
                        >
                            <FormCheckbox
                                name='reservationAppNotification'
                                control={control}
                            />
                        </StyledTableCell>
                    </TableRow>
                </TableBody>
            </Table>
            {loggedInUser?.role.includes('owner') && (
                <Accordion
                    // variant='outlined'
                    expanded={settingsOpen}
                    onChange={() => setSettingsOpen(!settingsOpen)}
                >
                    <AccordionSummary
                        expandIcon={<KeyboardArrowDown />}
                        sx={{ bgcolor: theme.palette.neutral.main }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                            }}
                        >
                            <Tune fontSize='small' />
                            <Typography>Advanced Options</Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        <FormControlLabel
                            label='Filter Experiences'
                            control={<Switch />}
                            checked={experiencesOpen}
                            onChange={handleExperiences}
                            sx={{ mr: 1 }}
                        />
                        <CustomTooltip
                            title={
                                <Typography>
                                    Filter which experiences to receive
                                    notifications for. If unselected, you will
                                    receive notifications for all experiences
                                </Typography>
                            }
                        >
                            <HelpOutline
                                sx={{
                                    color: 'text.primary',
                                    fontSize: '16px',
                                }}
                            />
                        </CustomTooltip>
                        {experiencesOpen && (
                            <>
                                {/* <InputLabel>Experiences</InputLabel> */}
                                <FormMultiAutocomplete
                                    name='reservationNotificationModules'
                                    control={control}
                                    options={
                                        experiences?.map(
                                            (experience: FormValues) => ({
                                                value: experience.id,
                                                label: experience.name,
                                                ...experience,
                                            }),
                                        ) || []
                                    }
                                    renderOption={(
                                        props,
                                        option: FormValues,
                                        { selected },
                                    ) => (
                                        <li key={option.id} {...props}>
                                            <ListItemAvatar>
                                                <Avatar
                                                    variant='rounded'
                                                    src={
                                                        option.photos?.[0]?.src
                                                    }
                                                />
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={option.name}
                                                secondary={`${option.streetAddress}, ${option.suburb}`}
                                            />
                                        </li>
                                    )}
                                />
                                <FormHelperText>
                                    Receive notifications relevant to selected
                                    experiences only
                                </FormHelperText>
                            </>
                        )}
                    </AccordionDetails>
                </Accordion>
            )}
        </div>
    );
};

export default ReservationNotifications;
